/**
 * Установка пароля по инвайту — `POST /auth/set-password`.
 *
 * Токен приходит в ссылке из письма: `/set-password?token=…`. Ручка сессию не
 * выдаёт, поэтому после успеха уводим на обычный вход.
 */
import { zodResolver } from '@hookform/resolvers/zod';
import { KeyRound, Loader2 } from 'lucide-react';
import { useMutation } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import { z } from 'zod';

import { setPassword } from '@/entities/auth/api/auth';
import { getApiErrorMessage } from '@/shared/api';
import { Button, Form } from '@/shared/ui';

import { AuthShell } from './AuthShell';
import { AuthTextField } from './AuthTextField';

/** Минимальная длина пароля — требование бэка, как и при регистрации. */
const MIN_PASSWORD = 8;

const schema = z
  .object({
    password: z
      .string()
      .min(MIN_PASSWORD, `Не короче ${MIN_PASSWORD} символов`),
    passwordConfirm: z.string().min(1, 'Повторите пароль'),
  })
  .refine((values) => values.password === values.passwordConfirm, {
    path: ['passwordConfirm'],
    message: 'Пароли не совпадают',
  });

type SetPasswordForm = z.infer<typeof schema>;

export default function SetPasswordPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token') ?? '';

  const form = useForm<SetPasswordForm>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', passwordConfirm: '' },
  });

  const submit = useMutation({
    mutationFn: (values: SetPasswordForm) =>
      setPassword({ token, password: values.password }),
    onSuccess: () => {
      toast.success('Пароль установлен, войдите с ним');
      navigate('/login', { replace: true });
    },
    onError: (error) =>
      toast.error(
        getApiErrorMessage(
          error,
          'Не удалось установить пароль — ссылка могла устареть'
        )
      ),
  });

  if (!token) {
    return (
      <AuthShell
        title="Ссылка неполная"
        subtitle="В адресе нет токена приглашения"
        footer={<Link to="/login">Вернуться ко входу</Link>}
      >
        <p className="text-sm text-slate-500">
          Откройте ссылку из письма целиком — в ней должен быть параметр
          <code className="mx-1 rounded bg-slate-100 px-1">token</code>.
        </p>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Новый пароль"
      subtitle="Придумайте пароль для входа в DOIT"
      footer={<Link to="/login">Уже есть пароль? Войти</Link>}
    >
      <Form {...form}>
        <form
          className="flex flex-col gap-4"
          onSubmit={form.handleSubmit((values) => submit.mutate(values))}
        >
          <AuthTextField
            control={form.control}
            name="password"
            label="Пароль"
            type="password"
            autoComplete="new-password"
            disabled={submit.isPending}
          />
          <AuthTextField
            control={form.control}
            name="passwordConfirm"
            label="Повторите пароль"
            type="password"
            autoComplete="new-password"
            disabled={submit.isPending}
          />

          <Button type="submit" size="lg" disabled={submit.isPending}>
            {submit.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <>
                <KeyRound className="size-4" /> Сохранить пароль
              </>
            )}
          </Button>
        </form>
      </Form>
    </AuthShell>
  );
}
